import React, { useState, useEffect, useCallback } from "react";
import { DatePicker, Select, Button, Checkbox, Empty, Result, message } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getGroups,
  getFacilitiesByGroup,
  getReportStatuses,
  createPeriodicalYearReport,
  getPresignedUrl,
} from "./periodicalApi";
import "./FacilityMultiSelect.css";

const { Option } = Select;
// mp016's "All" row; the old screen selects it by default.
const ALL_STATUS = "7";

const labelStyle = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

// Same filters as the old app's Reports screen: Year, Group, one or more
// facilities of that group (with Select All), and a status. All selected
// facilities go into a single PDF.
const Reports = () => {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilityIds, setFacilityIds] = useState([]);
  const [year, setYear] = useState(moment());
  const [statuses, setStatuses] = useState([]);
  const [status, setStatus] = useState(ALL_STATUS);
  const [loading, setLoading] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      const [groupsRes, statusRes] = await Promise.all([getGroups(), getReportStatuses()]);
      setGroups(!groupsRes.error && Array.isArray(groupsRes.result?.result) ? groupsRes.result.result : []);
      setStatuses(!statusRes.error && Array.isArray(statusRes.result?.result) ? statusRes.result.result : []);
    })();
  }, []);

  const resetResult = () => {
    setSearched(false);
    setReportUrl(null);
  };

  const onGroupChange = useCallback(async (value) => {
    setGroupId(value);
    setFacilityIds([]);
    setFacilities([]);
    resetResult();
    const { error, result } = await getFacilitiesByGroup(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const facilityValue = (f) => f.facility_id ?? f.id;
  const allSelected = facilities.length > 0 && facilityIds.length === facilities.length;

  const search = useCallback(async () => {
    if (!groupId || facilityIds.length === 0) {
      message.error("Please select a group and at least one facility first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    setReportUrl(null);
    const { error, result } = await createPeriodicalYearReport({ year: year.format("YYYY"), orgId: groupId, facilityIds, status });
    const reportPath = !error && result?.success !== false ? result?.result?.report_path : null;
    if (reportPath) {
      const signed = await getPresignedUrl(reportPath);
      const url = !signed.error && signed.result?.success && signed.result.result;
      if (url) setReportUrl(url);
      else message.error("The report was generated but its link couldn't be opened. Try again.");
    } else {
      message.error(result?.message || "No report could be generated for these filters.");
    }
    setLoading(false);
  }, [groupId, facilityIds, year, status]);

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>Year</div>
          <DatePicker picker="year" value={year} onChange={(d) => { if (d) { setYear(d); resetResult(); } }} allowClear={false} />
        </div>
        <div>
          <div style={labelStyle}>Group</div>
          <Select showSearch optionFilterProp="children" placeholder="Select a group" style={{ width: 200 }} value={groupId} onChange={onGroupChange}>
            {groups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
          </Select>
        </div>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div style={labelStyle}>
            Facility
            {facilityIds.length > 0 && <span style={{ marginLeft: 8, fontSize: 12, color: "#2F6FE0" }}>{facilityIds.length} selected</span>}
          </div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Select facility…"
            className="ws-facility-multi"
            style={{ width: "100%" }}
            value={facilityIds}
            onChange={(ids) => { setFacilityIds(ids); resetResult(); }}
            disabled={!groupId}
            maxTagCount={4}
            maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            notFoundContent={groupId ? "No facilities for this group" : "Select a group first"}
          >
            {facilities.map((f) => <Option key={facilityValue(f)} value={facilityValue(f)}>{f.facility_name ?? f.name}</Option>)}
          </Select>
          <Checkbox
            style={{ marginTop: 6 }}
            checked={allSelected}
            disabled={facilities.length === 0}
            onChange={(e) => { setFacilityIds(e.target.checked ? facilities.map(facilityValue) : []); resetResult(); }}
          >
            Select All
          </Checkbox>
        </div>
        <div>
          <div style={labelStyle}>Status</div>
          <Select value={status} onChange={(v) => { setStatus(v); resetResult(); }} style={{ width: 160 }}>
            {statuses.length === 0 && <Option value={ALL_STATUS}>All</Option>}
            {statuses.map((s) => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
          </Select>
        </div>
        <div>
          <div style={labelStyle}>&nbsp;</div>
          <Button type="primary" loading={loading} onClick={search}>Apply</Button>
        </div>
      </div>

      {searched && (
        reportUrl ? (
          <Result
            status="success"
            icon={<FilePdfOutlined />}
            title="Report generated"
            extra={
              <Button type="primary" href={reportUrl} target="_blank" rel="noreferrer">
                View / Download PDF
              </Button>
            }
          />
        ) : !loading ? (
          <Empty description="No report available for these filters." />
        ) : null
      )}
    </div>
  );
};

export default Reports;
